import React from "react";

/**
 * Reusable report content for Inv_Transaction types.
 * Renders master info and detail table based on config.
 */
const InvTransactionReportContent = ({
  title,
  data,
  detailColumns,
  extraInfoFields = [],
}) => {
  if (!data || !data.master) {
    return null;
  }

  const { master, details = [], record = {} } = data;
  const branchName = master.BranchName || record.BranchName || "";
  const departmentName = master.DepartmentName || record.DepartmentName || "";
  const transactionDate = master.TransactionDate
    ? typeof master.TransactionDate === "string"
      ? master.TransactionDate.split("T")[0]
      : master.TransactionDate
    : "";

  const reportStyles = {
    container: {
      background: "white",
      padding: "10px 30px 10px 10px",
    },
    title: {
      display: "flex",
      flex: 1,
      justifyContent: "center",
    },
    titleText: {
      color: "black",
      fontWeight: "bold",
    },
    headingSeparator: {
      borderBottom: "1px solid #000",
      margin: "10px 0 16px 0",
    },
    infoSection: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      justifyContent: "flex-start",
    },
    infoText: {
      fontSize: "14px",
      color: "black",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 10,
    },
    th: {
      border: "1px solid #000",
      padding: "8px",
      textAlign: "left",
      color: "black",
      fontWeight: "bold",
    },
    td: {
      border: "1px solid #000",
      padding: "8px",
      color: "black",
    },
    totalRow: {
      textAlign: "right",
      marginTop: 10,
      color: "black",
    },
    summaryRow: {
      border: "1px solid #000",
      padding: "8px",
      color: "black",
      fontWeight: "bold",
    },
  };

  const defaultInfoFields = [
    { key: "TransactionNo", label: "Transaction Number" },
    { key: "BranchName", label: "Branch", value: branchName },
    { key: "DepartmentName", label: "Department", value: departmentName },
    { key: "TransactionDate", label: "Date", value: transactionDate },
    {
      key: "IsSubmit",
      label: "Status",
      value: master.IsSubmit || record.IsSubmit ? "Post" : "Un-Post",
    },
  ];

  const getValue = (field, idx) => {
    if (field.value !== undefined) return field.value;
    const val = master[field.key] ?? record[field.key];
    if (field.key === "TransactionDate" && val)
      return typeof val === "string" ? val.split("T")[0] : val;
    if (field.key === "IsSubmit") return val ? "Post" : "Un-Post";
    return val ?? "";
  };

  const allInfoFields = [...defaultInfoFields, ...extraInfoFields];

  const renderCell = (row, col) => {
    const val = row[col.key];
    if (val == null) return "—";
    if (typeof val === "number") return val.toFixed(2);
    return String(val);
  };

  const numericCols = detailColumns.filter((c) => c.numeric);
  const nonNumericCount = detailColumns.filter((c) => !c.numeric).length;
  const totals = {};
  numericCols.forEach((c) => {
    totals[c.key] = details.reduce(
      (sum, row) => sum + (parseFloat(row[c.key]) || 0),
      0
    );
  });

  return (
    <div style={reportStyles.container}>
      <div style={reportStyles.title}>
        <h1 style={reportStyles.titleText}>{title}</h1>
      </div>
      <div style={reportStyles.headingSeparator} />

      <div style={reportStyles.infoSection}>
        {allInfoFields.map((field, idx) => (
          <p key={idx} style={reportStyles.infoText}>
            {field.label}: <b>{getValue(field, idx)}</b>
          </p>
        ))}
        {master.Description && (
          <p style={reportStyles.infoText}>
            Description: <b>{master.Description}</b>
          </p>
        )}
      </div>
      <br />
      <div>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              {detailColumns.map((col, idx) => (
                <th key={idx} style={reportStyles.th}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {details.map((row, index) => (
              <tr key={index}>
                {detailColumns.map((col, idx) => (
                  <td key={idx} style={reportStyles.td}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
            {details.length > 0 && numericCols.length > 0 && (
              <tr>
                <td style={reportStyles.summaryRow} colSpan={nonNumericCount}>
                  Total
                </td>
                {numericCols.map((c) => (
                  <td key={c.key} style={reportStyles.summaryRow}>
                    {totals[c.key].toFixed(2)}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
        <p style={reportStyles.totalRow}>
          Total Items : <b>{details.length}</b>
        </p>
      </div>
    </div>
  );
};

export default InvTransactionReportContent;
